import { ModeNames } from "@/modes/mode";
import { SceneFilter } from "@/types/scene-filter";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "./modes/flywalk-mode-slice";
import { AppThunk } from "./store";

/**
 * @returns Redux thunk that makes sure, that the given mode has a scene filter selected out of an array of allowed ones.
 * @param mode The mode to set the scene filter for
 * @param compatibleSceneFilters The array of compatible scene filters. The first entry is used as the default one.
 */
export function setCompatibleSceneFilterForMode(
  mode: ModeNames,
  compatibleSceneFilters: SceneFilter[],
): AppThunk<void> {
  return (dispatch, getState) => {
    const defaultSceneFilter = compatibleSceneFilters.at(0);
    // We couldn't compute a fitting scene filter. Keep the current one, as it is most likely to provide a valid view.
    if (!defaultSceneFilter) return;

    const state = getState();

    /**
     * @returns Whether it should be preferred to switch to overlay mode, instead of just switching to the default scene filter.
     * This is assumed, e.g. in case the user is looking at a CAD model, and switching to a point cloud element.
     * @param currentSceneFilter The current scene filter of the mode
     */
    function shouldSwitchToOverlay(currentSceneFilter: SceneFilter): boolean {
      return (
        ((currentSceneFilter === SceneFilter.PointCloud &&
          defaultSceneFilter === SceneFilter.Cad) ||
          (currentSceneFilter === SceneFilter.Cad &&
            defaultSceneFilter === SceneFilter.PointCloud)) &&
        compatibleSceneFilters.includes(SceneFilter.Overlay)
      );
    }

    switch (mode) {
      case "overview":
      case "walk": {
        const currentSceneFilter = selectFlyWalkSceneFilter(state);
        if (!compatibleSceneFilters.includes(currentSceneFilter)) {
          if (shouldSwitchToOverlay(currentSceneFilter)) {
            dispatch(setFlyWalkSceneFilter(SceneFilter.Overlay));
          } else {
            dispatch(setFlyWalkSceneFilter(defaultSceneFilter));
          }
        }
        break;
      }
    }
  };
}
